'use client';

import {
  AriaAttributes,
  Dispatch,
  JSX,
  Key,
  PropsWithChildren,
  SetStateAction,
  SyntheticEvent,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import clsx from 'clsx';
import { useInView } from 'react-intersection-observer';
import DallasText from '../Text/DallasText';
import useDidChange from '~/app/_ui/useDidChange';
import DallasTableSelectionProvider from './Providers/DallasTableSelectionProvider';
import { SortingOrder } from '@trucksmarter/shared';
import { Menu, MenuButton, MenuItem, MenuProvider } from '@ariakit/react';
import DallasSvgIconArrowUpwardOutlined from '../Icons/DallasSvgIconArrowUpwardOutlined';
import DallasSvgIconArrowDownwardOutlined from '../Icons/DallasSvgIconArrowDownwardOutlined';
import { DallasOptionItemContent } from '../Forms/DallasOptionItem/DallasOptionItem';
import optionItemStyles from '../Forms/DallasOptionItem/dallas-option-item.module.scss';
import DallasSvgIconSortOutlined from '../Icons/DallasSvgIconSortOutlined';
import ContainerSizeProvider from '../../shared/ContainerSizeProvider/ContainerSizeProvider';
import {
  DallasSortingOptionItem,
  DallasSortingValue,
} from '../Forms/dallas-sorting';

import styles from './DallasTable.module.scss';

const OnLastItemInViewContext = createContext<
  (() => Promise<void> | void) | null
>(null);

function useOnLastItemInView() {
  return useContext(OnLastItemInViewContext);
}

interface WithSelectionEnabledProps<T> {
  selectionMap: Map<Key, T>;
  onSelectionChange: Dispatch<SetStateAction<Map<Key, T>>>;
}

interface WithoutSelectionEnabledProps {
  selectionMap?: never;
  onSelectionChange?: never;
}

export interface RowTemplateProps<T> {
  dataItem: T;
  last: boolean;
}

interface CommonProps<T> {
  className?: string;
  headerRow: React.ReactNode;
  rowTemplate: (props: RowTemplateProps<T>) => JSX.Element;
  data: T[];
  /**
   * Function for getting the component key prop value used by react
   * in order to keep track of the state of the components.
   *
   * This function should be a REFERENCE THAT DOESN'T CHANGE
   * so, declare your function outside of the component you are using `<DallasTable />` in.
   */
  getDataItemKey: (dataItem: T) => Key;
  /**
   * Callback function to be called when the last item is in view.
   * Make sure it's reference doesn't change.
   */
  onLastItemInView?: () => Promise<void> | void;
  hasMoreItems?: boolean;
  loadingIndicator?: React.ReactNode;
}

type Props<T> = CommonProps<T> &
  (WithoutSelectionEnabledProps | WithSelectionEnabledProps<T>);

export function DallasTable<T>({
  className,
  headerRow,
  rowTemplate: RowTemplate,
  data,
  getDataItemKey,
  onLastItemInView,
  hasMoreItems,
  loadingIndicator,
  selectionMap,
  onSelectionChange,
}: Props<T>) {
  const bodyRows = useMemo(
    () =>
      data.map((dataItem, i) => (
        <RowTemplate
          key={getDataItemKey(dataItem)}
          dataItem={dataItem}
          last={i === data.length - 1}
        />
      )),
    [data, getDataItemKey, RowTemplate],
  );

  let tableContent = (
    <ContainerSizeProvider>
      {(containerRef) => (
        <div ref={containerRef} className={clsx(className, styles.root)}>
          <table className={styles.table}>
            <thead role="rowgroup">{headerRow}</thead>
            <tbody role="rowgroup">
              {bodyRows}
              {hasMoreItems === true && loadingIndicator}
            </tbody>
          </table>
        </div>
      )}
    </ContainerSizeProvider>
  );

  if (onLastItemInView != null) {
    tableContent = (
      <OnLastItemInViewContext.Provider value={onLastItemInView}>
        {tableContent}
      </OnLastItemInViewContext.Provider>
    );
  }

  if (selectionMap != null && onSelectionChange != null) {
    tableContent = (
      <DallasTableSelectionProvider
        selectionMap={selectionMap}
        onSelectionChange={onSelectionChange}
        getDataItemKey={getDataItemKey}
      >
        {tableContent}
      </DallasTableSelectionProvider>
    );
  }

  return tableContent;
}

export function DallasTableHeaderRow({ children }: PropsWithChildren) {
  return <tr className={styles.headerRow}>{children}</tr>;
}

interface RowProps extends AriaAttributes {
  className?: string;
  onClick?: () => void;
  selected?: boolean;
}

export const DallasTableRow = forwardRef<
  HTMLTableRowElement,
  PropsWithChildren<RowProps>
>(function DallasTableRow(
  { className, children, onClick, selected, ...ariaProps },
  ref,
) {
  return (
    <tr
      {...ariaProps}
      ref={ref}
      className={clsx(
        className,
        styles.row,
        onClick && styles.clickable,
        selected && styles.selected,
      )}
      onClick={onClick}
    >
      {children}
    </tr>
  );
});

interface VirtualRowProps extends RowProps {
  last: boolean;
}

export function DallasTableVirtualRow({
  className,
  children,
  last: isLast,
  onClick,
  selected,
  ...ariaProps
}: PropsWithChildren<VirtualRowProps>) {
  const [ref, isInView] = useInView();
  const onLastItemInView = useOnLastItemInView();
  const isLastInView = isLast && isInView;
  const didIsLastInViewChange = useDidChange(isLastInView);

  useEffect(() => {
    if (!didIsLastInViewChange && isLastInView) {
      onLastItemInView?.();
    }
  }, [didIsLastInViewChange, isLastInView, onLastItemInView]);

  return (
    <DallasTableRow
      {...ariaProps}
      ref={ref}
      className={className}
      onClick={isInView ? onClick : undefined}
      selected={selected}
    >
      {isInView && children}
    </DallasTableRow>
  );
}

interface CommonHeaderCellProps {
  className?: string;
  columnName?: string;
  colSpan?: number;
  children?: React.ReactElement;
  onClick?: (event: SyntheticEvent) => void;
}

interface NotSortableHeaderCellProps {
  sort?: never;
  sortOptions?: never;
  onSortChange?: never;
}

interface SortableHeaderCellProps<TField extends string> {
  columnName: string;
  children?: never;
  onClick?: never;
  sort: DallasSortingValue<TField>;
  sortOptions: DallasSortingOptionItem<TField>[];
  onSortChange: (sort: DallasSortingValue<TField>) => void;
}

type HeaderCellProps<TField extends string> = CommonHeaderCellProps &
  (SortableHeaderCellProps<TField> | NotSortableHeaderCellProps);

const SORT_ORDER_ICONS: Record<SortingOrder, React.ReactElement> = {
  asc: <DallasSvgIconArrowUpwardOutlined />,
  desc: <DallasSvgIconArrowDownwardOutlined />,
};

export function DallasTableHeaderCell<TField extends string>({
  className,
  columnName,
  colSpan,
  children,
  onClick,
  sort,
  sortOptions,
  onSortChange,
}: HeaderCellProps<TField>) {
  const ariaSort =
    sort != null && sortOptions
      ? getHeaderAriaSort(sort, sortOptions)
      : undefined;
  const caption = columnName && (
    <DallasText variant="captionM">{columnName}</DallasText>
  );

  return (
    <th
      className={clsx(styles.headerCell, className)}
      colSpan={colSpan}
      onClick={onClick}
      aria-sort={ariaSort}
    >
      {sort != null && sortOptions ? (
        sortOptions.length === 1 ? (
          <button
            className={styles.sortButton}
            type="button"
            onClick={() => onSortChange(sortOptions[0].value)}
          >
            {caption}
            {SORT_ORDER_ICONS[sortOptions[0].value.order ?? 'desc']}
          </button>
        ) : (
          <MenuProvider>
            <MenuButton className={styles.sortButton}>
              {caption}
              {sort != null && ariaSort != null ? (
                SORT_ORDER_ICONS[sort.order ?? 'desc']
              ) : (
                <DallasSvgIconSortOutlined />
              )}
            </MenuButton>
            <Menu className={styles.sortMenu}>
              {sortOptions.map((option) => (
                <MenuItem
                  key={`${option.value.field}-${option.value.order}`}
                  className={optionItemStyles.root}
                  onClick={() => onSortChange(option.value)}
                >
                  <DallasOptionItemContent
                    addOnStart={SORT_ORDER_ICONS[option.value.order ?? 'desc']}
                    label={option.label}
                    description={option.description}
                  />
                </MenuItem>
              ))}
            </Menu>
          </MenuProvider>
        )
      ) : (
        caption
      )}
      {children}
    </th>
  );
}

function getHeaderAriaSort<TField extends string>(
  sort: DallasSortingValue<TField>,
  options: DallasSortingOptionItem<TField>[],
) {
  const sortKeys = options.map(
    (option) => `${option.value.field}-${option.value.order}`,
  );

  if (sortKeys.includes(`${sort.field}-${sort.order}`)) {
    return sort.order === 'asc' ? 'ascending' : 'descending';
  }

  return undefined;
}

interface DataCellProps {
  className?: string;
  children?: React.ReactNode;
  colSpan?: number;
  onClick?: (event: SyntheticEvent) => void;
}
export function DallasTableDataCell({
  className,
  children,
  colSpan,
  onClick,
}: DataCellProps) {
  return (
    <td
      className={clsx(styles.dataCell, className)}
      colSpan={colSpan}
      onClick={onClick}
    >
      {children}
    </td>
  );
}
